import type { cartModalComponentMap } from "@components/cart-modal/cartModalComponentMap.js";
import type { ComponentProps } from "svelte";
import type { SvelteMap } from "svelte/reactivity";
import type { CartModalKind } from "@core/schema/CartModalKind.js";
import type { Country } from "@core/schema/Country.js";
import type { Coupon } from "@core/schema/Coupon.js";
import type { Currency } from "@core/schema/Currency.js";
import type { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
import type { OrderProduct } from "@core/schema/OrderProduct.js";
import type { OrderProductAttribute } from "@core/schema/OrderProductInfo.js";
import type { OrderValidationResponse } from "@core/schema/OrderValidation.js";
import type {
	ProductForCustomer,
	ProductOfVariantForCustomer,
	ProductVariantForCustomer,
} from "@core/schema/Product.js";
import type { SubscriptionInput, SubscriptionParams } from "@core/schema/Subscription.js";
import type { SubscriptionGiftData } from "@core/schema/SubscriptionGiftData.js";
import type { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
import type { SubscriptionValidationResponse } from "@core/schema/SubscriptionValidation.js";
import type { Tenant } from "@core/schema/Tenant.js";
import type { DeepPartial } from "@core/utils/DeepPartial.js";
import type { GraphQLClient } from "@core/utils/aws/appsync/GraphQLClient.js";
import type { StorageState } from "@core/utils/svelte/StorageState.svelte.js";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";
import type { CartClient } from "./CartClient.svelte.js";
import type { CartInitData } from "./CartInitData.js";
import type { CartStep, NextStep } from "./CartStep.js";
import type { CartInStep, CartState } from "./CartType.js";
import type { FieldsToAdvanceFrom } from "./FieldsToAdvanceFrom.js";
import { createInitialCart } from "./initialCart.js";

export const Cart = new (class Cart implements Readonly<CartClient> {
	private _cart = $state<CartClient>();
	private loadPromise: Promise<CartClient> | undefined;

	// Plain mutable fields not tied to real business logic - always local until loaded, then copied over.
	private _vatType = $state<EconomicSubjectVatType>();
	private _showModal = $state(false);
	private _showSubscriptionConfigurator = $state(false);
	private _cartModalProps = $state<Record<string, unknown>>({});

	public constructor() {
		if (typeof window !== "undefined") {
			this.loadPromise = this.load();
		}
	}

	private async load(): Promise<CartClient> {
		const { CartClient: RealCartClient } = await import("./CartClient.svelte.js");
		this._cart = new RealCartClient();
		this._cart.vatType = this._vatType;
		this._cart.showModal = this._showModal;
		this._cart.showSubscriptionConfigurator = this._showSubscriptionConfigurator;
		this._cart.cartModalProps = this._cartModalProps;
		return this._cart;
	}

	private async ensureLoaded(): Promise<CartClient> {
		return this._cart ?? (this.loadPromise ??= this.load());
	}

	public readonly currency = $derived<Currency | undefined>(this._cart ? this._cart.currency : undefined);
	public readonly destinationCountry = $derived<Country | "outsideEu" | undefined>(
		this._cart ? this._cart.destinationCountry : undefined,
	);
	public readonly itemCount = $derived(this._cart ? this._cart.itemCount : 0);
	public readonly price = $derived(
		this._cart
			? this._cart.price
			: {
					appliedDiscounts: {
						deliveryPrice: 0,
						discountsByTaxRate: [],
					},
					deliveryPrice: {
						taxRate: 0,
						withTax: 0,
						withoutTax: 0,
					},
					roundingAdjustment: 0,
					totalPrice: {
						withTax: 0,
						withoutTax: 0,
					},
				},
	);
	public readonly totalPrice = $derived(this.price?.totalPrice);
	public readonly deliveryPrice = $derived(this.price?.deliveryPrice);
	public readonly loaded = $derived(this._cart ? this._cart.loaded : false);
	public readonly appliedDiscounts = $derived(this.price?.appliedDiscounts);
	public readonly cartModalKind = $derived<CartModalKind | undefined>(
		this._cart ? this._cart.cartModalKind : undefined,
	);
	public readonly isEmpty = $derived(this._cart ? this._cart.isEmpty : true);
	public readonly restrictToCountries = $derived<Country[] | undefined>(
		this._cart ? this._cart.restrictToCountries : undefined,
	);
	public readonly subscriptionPrice = $derived(
		this._cart ? this._cart.subscriptionPrice : { taxRate: 0, withoutTax: 0, withTax: 0 },
	);
	public readonly state = $derived<CartState | undefined>(this._cart ? this._cart.state : undefined);
	public readonly orderProducts = $derived<CartState["order"]["products"]>(this._cart ? this._cart.orderProducts : []);
	public readonly productsInOrder = $derived<ProductOfVariantForCustomer[]>(
		this._cart ? this._cart.productsInOrder : [],
	);
	public readonly couponCode = $derived<string | undefined>(this._cart ? this._cart.couponCode : undefined);
	public readonly coupons = $derived<Coupon[]>(this._cart ? this._cart.coupons : []);

	public get vatType(): EconomicSubjectVatType | undefined {
		return this._cart ? this._cart.vatType : this._vatType;
	}

	public set vatType(value: EconomicSubjectVatType | undefined) {
		this._vatType = value;
		if (this._cart) {
			this._cart.vatType = value;
		}
	}

	public get showModal(): boolean {
		return this._cart ? this._cart.showModal : this._showModal;
	}

	public set showModal(value: boolean) {
		this._showModal = value;
		if (this._cart) {
			this._cart.showModal = value;
		}
	}

	public get showSubscriptionConfigurator(): boolean {
		return this._cart ? this._cart.showSubscriptionConfigurator : this._showSubscriptionConfigurator;
	}

	public set showSubscriptionConfigurator(value: boolean) {
		this._showSubscriptionConfigurator = value;
		if (this._cart) {
			this._cart.showSubscriptionConfigurator = value;
		}
	}

	public get cartModalProps(): Record<string, unknown> {
		return this._cart ? this._cart.cartModalProps : this._cartModalProps;
	}

	public set cartModalProps(value: Record<string, unknown>) {
		this._cartModalProps = value;
		if (this._cart) {
			this._cart.cartModalProps = value;
		}
	}

	public get mothersBlendConstantPriceEnabled(): boolean {
		return this._cart?.mothersBlendConstantPriceEnabled ?? false;
	}

	public set mothersBlendConstantPriceEnabled(value: boolean) {
		if (this._cart) {
			this._cart.mothersBlendConstantPriceEnabled = value;
		}
	}

	public get cart(): StorageState<CartState> | null {
		return this._cart?.cart ?? null;
	}

	public set cart(value: StorageState<CartState> | null) {
		if (this._cart) {
			this._cart.cart = value;
		}
	}

	public get currentCartModalKind(): CartModalKind | undefined {
		return this._cart?.currentCartModalKind;
	}

	public set currentCartModalKind(value: CartModalKind | undefined) {
		if (this._cart) {
			this._cart.currentCartModalKind = value;
		}
	}

	public get couponCache(): SvelteMap<string, Coupon | null> {
		if (!this._cart) {
			throw new Error("Cart not loaded yet");
		}
		return this._cart.couponCache;
	}

	public async completeSubscription(tenant: Tenant, graphQL: GraphQLClient, input: SubscriptionInput): Promise<void> {
		const cart = await this.ensureLoaded();
		await cart.completeSubscription(tenant, graphQL, input);
	}

	public openSubscriptionConfigurator(kind?: SubscriptionKind): void {
		if (this.state !== undefined && kind !== undefined && kind !== this.state.subscription.kind) {
			this.saveSubscriptionFields({ kind });
			this.state.subscriptionModalStep = 0;
		}
		this.showSubscriptionConfigurator = true;
	}

	public async init(settings: GlobalSettings, data: CartInitData): Promise<void> {
		const cart = await this.ensureLoaded();
		await cart.init(settings, data);
	}

	public getCorrectCartInStepOrThrow<Step extends CartStep>(inStep: Step): CartInStep<Step> {
		if (!this._cart) {
			return createInitialCart() as CartInStep<Step>;
		}
		return this._cart.getCorrectCartInStepOrThrow(inStep);
	}

	public getCorrectCartInStepOrRedirect<Step extends CartStep>(
		tenant: Tenant,
		inStep: Step,
	): { cartInStep: CartInStep<Step>; redirected: boolean } {
		if (!this._cart) {
			return { cartInStep: createInitialCart() as CartInStep<Step>, redirected: false };
		}
		return this._cart.getCorrectCartInStepOrRedirect(tenant, inStep);
	}

	public addSubscription(onAdded?: () => void): void {
		this._cart?.addSubscription(onAdded);
	}

	public addProduct(
		product: ProductOfVariantForCustomer,
		attributes: OrderProductAttribute[],
		quantity: number,
		insertionIndex?: number,
		onAdded?: (newlyAdded: boolean) => void,
	): void {
		this._cart?.addProduct(product, attributes, quantity, insertionIndex, onAdded);
	}

	public removeProduct(orderProduct: OrderProduct, quantity?: number): void {
		this._cart?.removeProduct(orderProduct, quantity);
	}

	public setProductQuantity(orderProduct: OrderProduct, newQuantity: number): void {
		this._cart?.setProductQuantity(orderProduct, newQuantity);
	}

	public updateProductInfoInBasket(orderProduct: OrderProduct, updated: ProductForCustomer): void {
		this._cart?.updateProductInfoInBasket(orderProduct, updated);
	}

	public updateProductVariantInBasket(orderProduct: OrderProduct, variantId: ProductVariantForCustomer["id"]): void {
		this._cart?.updateProductVariantInBasket(orderProduct, variantId);
	}

	public clear(): void {
		this._cart?.clear();
	}

	public regenerateIdempotencyKey(): void {
		this._cart?.regenerateIdempotencyKey();
	}

	public async validateCoupon(globalSettings: GlobalSettings, code: string): Promise<Coupon | null> {
		const cart = await this.ensureLoaded();
		return cart.validateCoupon(globalSettings, code);
	}

	public findProduct(orderProduct: OrderProduct): ProductOfVariantForCustomer | undefined {
		return this._cart?.findProduct(orderProduct);
	}

	public saveOrderFields(fields: DeepPartial<CartState["order"]>): void {
		this._cart?.saveOrderFields(fields);
	}

	public saveSubscriptionFields(fields: Partial<SubscriptionParams>): void {
		this._cart?.saveSubscriptionFields(fields);
	}

	public saveGiftDataFields(fields: Partial<SubscriptionGiftData>): void {
		this._cart?.saveGiftDataFields(fields);
	}

	public saveActivateGiftImmediately(value: boolean): void {
		this._cart?.saveActivateGiftImmediately(value);
	}

	public advanceToNextStep<Step extends keyof { [K in CartStep as NextStep<K> extends never ? never : K]: never }>(
		currentStep: Step,
		fields: FieldsToAdvanceFrom<Step>,
		noRedirect = false,
	): CartInStep<NextStep<Step>> {
		if (!this._cart) {
			throw new Error("Cart not loaded yet");
		}
		return this._cart.advanceToNextStep(currentStep, fields, noRedirect);
	}

	public async completeOrder(globalSettings: GlobalSettings): Promise<void> {
		const cart = await this.ensureLoaded();
		await cart.completeOrder(globalSettings);
	}

	public patchInvalidSubscription(response: SubscriptionValidationResponse): void {
		this._cart?.patchInvalidSubscription(response);
	}

	public patchInvalidOrder(response: OrderValidationResponse): void {
		this._cart?.patchInvalidOrder(response);
	}

	public openCartModal<K extends CartModalKind>(
		kind: K,
		props: ComponentProps<(typeof cartModalComponentMap)[K]>,
	): void {
		this._cart?.openCartModal(kind, props);
	}

	public closeCartModal(): void {
		this._cart?.closeCartModal();
	}

	public dispose(): void {
		this._cart?.dispose();
	}
})();
